import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, switchMap } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { TabPermissionService } from 'app/core/auth/tab-permission.service';
import { StorageUsageService } from 'app/services/dashboard/storage-usage.service';
import { StorageLimitAlertService } from 'app/services/dashboard/storage-limit-alert.service';
import { MemberAvatarsService } from 'app/services/secretary/member/member-avatars.service';
import { environment } from 'environments/environment';


@Injectable({
    providedIn: 'root',
})
export class AuthService
{
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _navigationService: NavigationService,
        private _tabPermissionService: TabPermissionService,
        private _storageUsageService: StorageUsageService,
        private _storageLimitAlertService: StorageLimitAlertService,
        private _memberAvatarsService: MemberAvatarsService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token and user object
     */
    set accessToken(token: string)
    {
        localStorage.setItem('token', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('token') ?? '';
    }

    /**
     * Set user param on local storage of browser
     * @param user
     */
    set setLSUser(user: string)
    {
        localStorage.setItem('user', JSON.stringify(user));
    }

    /**
     * Get user param on local storage of browser
     */
    get getLSUser(): string
    {
        return localStorage.getItem('user') ?? '';
    }


    /**
     * Get church param on local storage of browser
     * @param church
     */
    set setLSChurch(church: string)
    {
        localStorage.setItem('church', JSON.stringify(church));
    }


    /**
     * Get church param on local storage of browser
     */
    get getLSChurch(): string
    {
        return localStorage.getItem('church') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            this._authenticated = false;
        }

        // Reset dos caches de menu/permissões da sessão anterior no INÍCIO do
        // login: evita herança cross-user na mesma sessão SPA (ex.: usuário A
        // logado com menu/permissões cacheadas e login como usuário B sem
        // signOut prévio — B herda A e leva 403 → expulsão /home).
        this._navigationService.reset();
        this._tabPermissionService.reset();

        return this._httpClient.post(environment.config.apiUrl + '/v1/login', credentials).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.data.token;
                this.setLSUser = response.data.user;
                this.setLSChurch = response.data.church;

                if (response.data.permissions)
                {
                    localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Set object attributes with response
                this._userService.user = response.data.user;

                // Armazenamento: nova sessão → zera o cache e dispara a busca
                // ÚNICA do snapshot (GET /dashboard/storage-usage). O alerta de
                // limite >= 80% é checado logo após essa busca (gatilho pós-login).
                this._storageUsageService.reset();
                this._storageUsageService.load();
                this._storageLimitAlertService.checkOnLogin();

                // Avatares do card da igreja (sidebar): a busca única por sessão
                // é disparada no bootstrap do shell autenticado (vertical.component
                // → load() idempotente com gate de permissão C7). Aqui apenas o
                // cache é zerado — evita herança cross-user na mesma sessão SPA.
                this._memberAvatarsService.reset();

                // Return a new observable with the response
                return of(response);
            })
        );
    }



    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('church');
        localStorage.removeItem('permissions');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Reset navigation cache so menu is fetched on next login
        this._navigationService.reset();

        // Reset permissions cache so next login gets fresh data
        this._tabPermissionService.reset();

        // Reset do cache do snapshot de armazenamento — a próxima sessão
        // (login) refaz a busca única com dados frescos do tenant.
        this._storageUsageService.reset();

        // Reset do cache dos avatares do card da igreja — a próxima sessão
        // refaz a coleta paginada com dados frescos do tenant.
        this._memberAvatarsService.reset();

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Se já está autenticado mas faltam dados no localStorage, retorna false
        if (this._authenticated && (!this.accessToken || !this.getLSChurch || !this.getLSUser)) {
            return of(false);
        }

        // Se já está autenticado e tem todos os dados, retorna true
        if (this._authenticated && this.accessToken && this.getLSChurch && this.getLSUser) {
            return of(true);
        }

        // Se tem token e dados no localStorage, considera autenticado
        // O backend vai rejeitar requisições com token inválido/expirado via interceptor
        if (this.accessToken && this.getLSChurch && this.getLSUser) {
            this._authenticated = true;
            return of(true);
        }

        // Sem token, não está autenticado
        return of(false);
    }


    /**
     * Check role of user before continue
     */
    checkRole(): Observable<boolean>
    {
        return of(false);
    }
}
